import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { RootState } from './store';

type SEODoc = {
  id: string;
  headTags?: string;
  bodyTags?: string;
  footerTags?: string;
};

type SEOState = {
  id: string | null;
  headTags: string;
  bodyTags: string;
  footerTags: string;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
};

const initialState: SEOState = {
  id: null,
  headTags: '',
  bodyTags: '',
  footerTags: '',
  status: 'idle',
  error: null,
};

// 🔄 Inject into <head>
export const injectSEO = (state: SEOState) => {
  const inject = (html: string, label: string) => {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    Array.from(wrapper.children).forEach((el) => {
      document.head.appendChild(el);
    });
  };

  if (state.headTags) inject(state.headTags, 'headTags');
  if (state.bodyTags) inject(state.bodyTags, 'bodyTags');
  if (state.footerTags) inject(state.footerTags, 'footerTags');
};

// 📥 FETCH SEO
export const fetchSEO = createAsyncThunk<SEODoc | null>(
  'seo/fetchSEO',
  async () => {
    const snapshot = await getDocs(collection(db, 'seo'));
    if (snapshot.empty) {
      //console.warn('[fetchSEO] No SEO document found in Firestore.');
      return null;
    }

    const docSnap = snapshot.docs[0];
    const data = docSnap.data() as Omit<SEODoc, 'id'>;


    return { id: docSnap.id, ...data };
  }
);

// 💾 UPSERT SEO
export const upsertSEO = createAsyncThunk<
  SEODoc,
  { headTags: string; bodyTags: string; footerTags: string },
  { state: RootState }
>('seo/upsertSEO', async (data, { getState }) => {
  const { seo } = getState();
  const docId = seo.id;


  if (docId) {
    await updateDoc(doc(db, 'seo', docId), data);
    return { id: docId, ...data };
  } else {
    const ref = await addDoc(collection(db, 'seo'), data);
    return { id: ref.id, ...data };
  }
});

const seoSlice = createSlice({
  name: 'seo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSEO.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSEO.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) {
          state.id = action.payload.id;
          state.headTags = action.payload.headTags || '';
          state.bodyTags = action.payload.bodyTags || '';
          state.footerTags = action.payload.footerTags || '';

          if (typeof window !== 'undefined') {
            injectSEO(state);
          }
        }
      })
      .addCase(fetchSEO.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to load SEO data';
        console.error('[seoSlice] fetchSEO failed:', state.error);
      })
      .addCase(upsertSEO.fulfilled, (state, action) => {
        state.id = action.payload.id;
        state.headTags = action.payload.headTags || '';
        state.bodyTags = action.payload.bodyTags || '';
        state.footerTags = action.payload.footerTags || '';

        if (typeof window !== 'undefined') {
          injectSEO(state);
        }
      });
  },
});

export default seoSlice.reducer;
